<template>
  <VCard>
    <div class="flex flex-col gap-4 bg-white rounded-md px-4 py-2 w-full">
      <div class="flex flex-row flex-wrap justify-between">
        <div class="flex flex-row flex-wrap items-baseline gap-2 text-primary-text flex-1 py-2">
          <p
            class="text-primary font-semibold text-h5 text-truncate cursor-pointer"
            data-testid="patient-name"
            @click="redirectToPatientDetails"
          >
            {{ getFormattedPatientName(caze.patient) }}
          </p>
          <PatientDetails :caze="caze" class="text-body-2" previewMode />
        </div>

        <div class="flex flex-row flex-wrap items-center gap-2">
          <VBtn
            v-if="canCreateConsultations && isConnectEnabled"
            :active="false"
            :class="isConsultationDisabled ? 'opacity-50' : 'opacity-100'"
            color="primary"
            variant="text"
            :disabled="isConsultationDisabled"
            @click="onCreateConsultation"
          >
            <template #prepend>
              <img
                src="/calendar-add.svg"
                alt="Create consultation"
                class="place-self-center w-[20px] h-[20px]"
              />
            </template>

            {{ t('Create consultation') }}
          </VBtn>

          <VBtn
            v-if="canCreateConsultations && isConnectEnabled"
            :active="false"
            :class="caze.emergencyRoutingUnavailable ? 'opacity-50' : 'opacity-100'"
            :disabled="caze.emergencyRoutingUnavailable"
            color="secondary"
            variant="text"
            data-testid="emergency-button"
            @click="emit('emergency-click', caze.id)"
          >
            <template #prepend>
              <VIcon color="secondary" size="20" icon="emergency" class="material-icons-round" />
            </template>

            {{ t('Emergency Call') }}
          </VBtn>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div
          class="gap-2 justify-between grid"
          :class="{
            'grid-cols-3 md:grid-cols-5 xl:grid-cols-10': !isSidebarVisible,
            'grid-cols-2 md:grid-cols-4 xl:grid-cols-7': isSidebarVisible
          }"
        >
          <!-- AKI Tile -->
          <div
            class="bg-[#F3F4F7] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
            data-testid="aki-risk-tile"
          >
            <p class="text-caption text-[#206683] flex items-center gap-1">
              {{ t('AKI') }}
              <VTooltip
                location="bottom left"
                :open-delay="500"
                :disabled="!hasAcceptedAkiLimitation"
              >
                <template #activator="{ props: infoActivatorProps }">
                  <span
                    v-bind="infoActivatorProps"
                    @click="$emit('open-aki-sidebar')"
                    class="inline-flex items-center justify-center align-middle leading-none cursor-pointer"
                    :class="[
                      {
                        'text-primary-600 font-semibold p-0': !hasAcceptedAkiLimitation
                      }
                    ]"
                  >
                    <VIcon
                      v-if="hasAcceptedAkiLimitation"
                      icon="info"
                      color="primary-600"
                      class="material-icons-outlined"
                      size="18"
                    />
                    <VIcon v-else icon="visibility_off" class="material-icons-outlined" size="18" />
                  </span>
                </template>
                <span>{{ t('Display Information') }}</span>
              </VTooltip>
            </p>
            <button
              v-if="!hasAcceptedAkiLimitation"
              data-testid="open-aki-sidebar-btn"
              @click="$emit('open-aki-sidebar')"
            >
              <VIcon
                class="material-icons-outlined"
                icon="visibility_off"
                size="18px"
                color="fg-disabled"
              />
            </button>
            <VTooltip
              v-else
              :text="akiPredictionTooltipText"
              location="bottom center"
              :open-delay="500"
              :offset="-5"
            >
              <template #activator="{ props: activatorProps }">
                <p
                  v-bind="activatorProps"
                  class="text-subtitle-2 font-bold"
                  :style="akiRiskLabelStyle"
                  data-testid="aki-risk-label"
                >
                  {{ akiRiskLabel }}
                </p>
              </template>
            </VTooltip>
          </div>

          <!-- Vital Signs -->
          <VTooltip
            v-for="(vital, index) in vitalSignsCards"
            :key="index"
            :text="vital.displayDate"
            :location="'bottom center'"
            :open-delay="500"
            :offset="-5"
          >
            <template #activator="{ props: activatorProps }">
              <div
                v-bind="activatorProps"
                class="bg-[#F3F4F7] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
              >
                <p class="text-caption text-[#206683]">{{ vital.label }}</p>
                <p class="text-subtitle-2 font-bold text-[#17485D]">
                  {{ vital.value }}
                </p>
              </div>
            </template>
          </VTooltip>

          <div
            class="bg-[#F3F4F7] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
          >
            <p class="text-caption text-[#206683] text-nowrap">
              {{ getLabelWithDefaultUnit(Parameter.Creatinine, false) }}
            </p>
            <VTooltip
              :text="CREATININE.displayDate"
              location="bottom center"
              :open-delay="500"
              :offset="-5"
            >
              <template #activator="{ props: activatorProps }">
                <p v-bind="activatorProps" class="text-subtitle-2 font-bold text-[#17485D]">
                  {{ formatValue(CREATININE.displayValue, NumberFormat.DECIMAL) }}
                </p>
              </template>
            </VTooltip>
          </div>

          <div
            class="bg-[#F3F4F7] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
          >
            <VTooltip
              :text="getParameterLabel(Parameter.Urea)"
              location="bottom center"
              :open-delay="500"
              :offset="-5"
            >
              <template #activator="{ props: activatorProps }">
                <p v-bind="activatorProps" class="text-caption text-[#206683]">
                  {{ getLabelWithDefaultUnit(Parameter.Urea, false) }}
                </p>
              </template>
            </VTooltip>
            <VTooltip
              :text="UREA.displayDate"
              location="bottom center"
              :open-delay="500"
              :offset="-5"
            >
              <template #activator="{ props: activatorProps }">
                <p v-bind="activatorProps" class="text-subtitle-2 font-bold text-[#17485D]">
                  {{ formatValue(UREA.displayValue, NumberFormat.DECIMAL) }}
                </p>
              </template>
            </VTooltip>
          </div>

          <VTooltip
            v-if="isEdsEnabled"
            :text="edsTooltipText"
            location="bottom center"
            :open-delay="500"
            :offset="-5"
            :disabled="!hasAcceptedEdsLimitation"
          >
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="bg-[#F5F5F5] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
                data-testid="eds-score-tile"
              >
                <span class="text-caption text-[#206683]">
                  {{ t('EDS Score') }}
                  <button v-if="hasAcceptedEdsLimitation" @click="emit('open-eds-sidebar')">
                    <VIcon
                      icon="info"
                      color="primary-600"
                      class="material-icons-outlined ml-1"
                      size="18"
                    />
                  </button>
                </span>
                <span
                  data-testid="eds-score-value"
                  class="text-subtitle-2 font-bold text-[#17485D]"
                >
                  <VBtn
                    v-if="!hasAcceptedEdsLimitation"
                    data-testid="open-eds-sidebar-btn"
                    class="px-1.5"
                    size="small"
                    height="22"
                    @click="emit('open-eds-sidebar')"
                  >
                    <VIcon icon="lock" class="material-icons-outlined" size="14" />
                    {{ t('Show') }}
                  </VBtn>
                  <template v-else>
                    {{ formatValue(edsScore?.scoreValue, NumberFormat.INTEGER) }}
                  </template>
                </span>
              </div>
            </template>
          </VTooltip>

          <div
            v-for="organSupport in organSupportList"
            :key="organSupport"
            class="bg-[#F5F5F5] flex flex-col items-center justify-center rounded-md p-2 w-full xl:w-[100px]"
            data-testid="organ-support-tile"
          >
            <p class="text-caption text-[#206683]">{{ organSupport }}</p>
            <div class="flex gap-1 align-center">
              <span class="organ-support-dot" />
              <span class="text-caption font-bold">{{ t('Active') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </VCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { ClinicalScoreResultDto } from '@/generated'
import type { AkiCaseWithPredictionResult } from '@/stores/akiPredictions'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { usePluginsStore } from '@/stores/plugins'
import { useUserStore } from '@/stores/user'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { routeNames } from '@/router/routeNames'
import { usePatientAkiParams } from '@/components/patient/composables/usePatientAkiParams'
import { usePatientVitals } from '@/components/patient/composables/usePatientVitals'
import { useFormatValue } from '@/composables/useFormatValue'
import { NumberFormat } from '@/i18n/i18n'
import PatientDetails from '@/components/patient/components/atoms/PatientDetails.vue'
import { parseOrganSupportDTO } from '@/util/parseOrganSupportDTO'
import { useAkiPlugin } from '@/composables/useAkiPlugin'

const { t } = useI18n()
const router = useRouter()
const { getLabelWithDefaultUnit, getParameterLabel } = useParameterLabel()
const { getFormattedPatientName } = useFormattedPatientDetails()
const { formatValue } = useFormatValue()
const { hasAcceptedAkiLimitation, getAkiPredictionTooltipText } = useAkiPlugin()
const { hasAcceptedEdsLimitation, getEdsScoreTooltipText } = useEdsPlugin()
const { isEdsEnabled, isConnectEnabled } = storeToRefs(usePluginsStore())

const userStore = useUserStore()
const { canCreateConsultations } = storeToRefs(userStore)
const { isConsultationDisabledForPatient } = userStore
const noticeStore = useNoticeStore()

const props = defineProps<{
  patientData: AkiCaseWithPredictionResult
  edsScore?: ClinicalScoreResultDto
  isSidebarVisible: boolean
}>()

const emit = defineEmits<{
  (e: 'open-eds-sidebar'): void
  (e: 'open-aki-sidebar'): void
  (e: 'emergency-click', caseId: string): void
}>()

const caze = computed(() => props.patientData.case)

const isConsultationDisabled = computed(() => {
  return isConsultationDisabledForPatient(caze.value.hospital?.id)
})

async function onCreateConsultation() {
  try {
    await router.push({
      name: routeNames.NEW_CONSULTATION,
      query: { caseId: caze.value.id }
    })
  } catch (error) {
    console.error((error as Error).message)
    noticeStore.add({
      type: NoticeType.ERROR,
      message: 'Failed to navigate to consultation request'
    })
  }
}

const edsTooltipText = computed(() => getEdsScoreTooltipText(props.edsScore))

const reactiveAkiParams = computed(() => {
  return [
    ...(props.patientData?.usedInputs?.vitalSigns?.heartRate || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.respiratoryRate || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.meanArterialPressure ||
      []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.systolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.invasiveBloodPressure?.diastolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.meanArterialPressure ||
      []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.systolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.nonInvasiveBloodPressure?.diastolic || []),
    ...(props.patientData?.usedInputs?.vitalSigns?.spo2 || []),
    ...(props.patientData?.usedInputs?.laboratory?.bilirubin || []),
    ...(props.patientData?.usedInputs?.laboratory?.crp || []),
    ...(props.patientData?.usedInputs?.laboratory?.creatinine || []),
    ...(props.patientData?.usedInputs?.laboratory?.ph || []),
    ...(props.patientData?.usedInputs?.laboratory?.lactate || []),
    ...(props.patientData?.usedInputs?.laboratory?.leukocytes || []),
    ...(props.patientData?.usedInputs?.laboratory?.potassium || []),
    ...(props.patientData?.usedInputs?.laboratory?.urea || [])
  ]
})

const reactiveVitalSigns = computed(() => props.patientData.vitalSigns ?? [])

const { CREATININE, UREA } = usePatientAkiParams(reactiveAkiParams)
const { INV_BP, NON_INV_BP, HEART_RATE, SPO2, RR, TEMP } = usePatientVitals(reactiveVitalSigns)

interface VitalSignCardData {
  label: string
  value: string
  displayDate: string
}

const vitalSignsCards = computed<VitalSignCardData[]>(() => [
  {
    label: getLabelWithDefaultUnit(
      INV_BP.value.hasMeasurement
        ? Parameter.InvasiveBloodPressure
        : Parameter.NonInvasiveBloodPressure,
      true
    ),
    value: INV_BP.value.hasMeasurement
      ? formatValue(INV_BP.value.displayValue)
      : formatValue(NON_INV_BP.value.displayValue),
    displayDate: INV_BP.value.hasMeasurement
      ? INV_BP.value.displayDate
      : NON_INV_BP.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.HeartRate, true),
    value: formatValue(HEART_RATE.value.displayValue, NumberFormat.INTEGER),
    displayDate: HEART_RATE.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.OxygenSaturationSPO2, true),
    value: formatValue(SPO2.value.displayValue, NumberFormat.INTEGER),
    displayDate: SPO2.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.RespiratoryRate, true),
    value: formatValue(RR.value.displayValue, NumberFormat.INTEGER),
    displayDate: RR.value.displayDate
  },
  {
    label: getLabelWithDefaultUnit(Parameter.Temperature, true),
    value: formatValue(TEMP.value.displayValue),
    displayDate: TEMP.value.displayDate
  }
])

const organSupportList = computed<string[]>(() => {
  if (!props.patientData.organSupport) return []
  return parseOrganSupportDTO(props.patientData.organSupport, t)
})

const akiRiskLabel = computed(() => {
  switch (props.patientData.predictionResult?.probability) {
    case 'VERY_HIGH':
      return t('Very High Risk')
    case 'HIGH':
      return t('High Risk')
    default:
      return '–'
  }
})

const akiRiskLabelStyle = computed(() => {
  const colorCode = props.patientData.predictionResult?.probabilityColorCode

  if (!colorCode) {
    return { color: '#6B7280' }
  }

  return { color: colorCode }
})

const akiPredictionTooltipText = computed(() =>
  getAkiPredictionTooltipText(props.patientData.predictionResult)
)

async function redirectToPatientDetails() {
  await router.push({
    name: routeNames.PATIENT_DETAILS,
    params: { caseId: props.patientData.case.id }
  })
}
</script>

<style scoped lang="scss">
.organ-support-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #17b26a;
}

.v-tooltip > ::v-deep(.v-overlay__content) {
  background: black;
}
</style>
